"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { PasswordStrings } from "@/components/tools/password-strength-checker";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  ATTACKS,
  type AttackId,
  crackTime,
  type PatternMatch,
  type SuggestionId,
} from "@/lib/tool-kit/ciphers/password-strength";

/** The password checker's tables and lists, below the verdict. */

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const SUPERSCRIPT = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const SPACE = / /g;
const LONG_COMPACT: Intl.NumberFormatOptions = {
  notation: "compact",
  compactDisplay: "long",
};

/** 4,976, or 1.2 × 10¹⁵ once the number is too long to read. */
export function guessText(log10: number, fmt: Format): string {
  if (log10 < 6) {
    return fmt(Math.round(10 ** log10));
  }
  const exponent = Math.floor(log10);
  const mantissa = 10 ** (log10 - exponent);
  const power = [...String(exponent)]
    .map((digit) => SUPERSCRIPT.charAt(Number(digit)))
    .join("");
  return `${fmt(mantissa, { maximumFractionDigits: 1 })} × 10${power}`;
}

function timeText(seconds: number, fmt: Format, strings: PasswordStrings) {
  const time = crackTime(seconds);
  if (time.kind === "instant") {
    return strings.instant;
  }
  if (time.kind === "eons") {
    return strings.eons;
  }
  return fmt(time.value, {
    style: "unit",
    unit: time.unit,
    unitDisplay: "long",
    maximumFractionDigits: 0,
    ...(time.value >= 1e6 ? LONG_COMPACT : {}),
  });
}

function speedText(perSecond: number, fmt: Format, strings: PasswordStrings) {
  if (perSecond < 1) {
    return t(strings.perHour, { count: fmt(Math.round(perSecond * 3600)) });
  }
  return t(strings.perSecond, { count: fmt(perSecond, LONG_COMPACT) });
}

export function CrackTable({
  seconds,
  strings,
}: {
  seconds: Readonly<Record<AttackId, number>>;
  strings: PasswordStrings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.attackColumn}
            </th>
            <th className={TH} scope="col">
              {strings.speedColumn}
            </th>
            <th className={TH} scope="col">
              {strings.timeColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {ATTACKS.map((attack) => (
            <tr key={attack.id}>
              <td className={TD}>{strings[attack.id]}</td>
              <td className={`${TD} whitespace-nowrap`}>
                {speedText(attack.perSecond, fmt, strings)}
              </td>
              <td className={`${TD} font-semibold`}>
                {timeText(seconds[attack.id], fmt, strings)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function notesFor(match: PatternMatch, strings: PasswordStrings): string[] {
  return [
    match.l33t ? strings.matchL33t : null,
    match.reversed ? strings.matchReversed : null,
    match.capitals ? strings.matchCapitals : null,
  ].filter((note) => note !== null);
}

function wordText(match: PatternMatch, strings: PasswordStrings, fmt: Format) {
  const word = match.word ?? "";
  if (match.kind === "wordlike") {
    return t(strings.matchWordlike, { word });
  }
  const base: Record<string, string> = {
    passwords: t(strings.matchPassword, { rank: fmt(match.rank ?? 0) }),
    english: t(strings.matchEnglish, { word }),
    names: t(strings.matchName, { word }),
    surnames: t(strings.matchSurname, { word }),
  };
  return base[match.dictionary ?? "english"];
}

function describe(
  match: PatternMatch,
  strings: PasswordStrings,
  locale: string,
  fmt: Format
): string {
  switch (match.kind) {
    case "dictionary":
    case "wordlike":
      return [wordText(match, strings, fmt), ...notesFor(match, strings)].join(
        ", "
      );
    case "repeat":
      return tPlural(strings.matchRepeat, locale, match.repeatCount ?? 1, {
        unit: match.repeatUnit ?? "",
        count: fmt(match.repeatCount ?? 1),
      });
    case "sequence":
      return strings.matchSequence;
    case "keyboard":
      return strings.matchKeyboard;
    case "year":
      return strings.matchYear;
    case "date":
      return strings.matchDate;
    default:
      return strings.matchRandom;
  }
}

export function PatternList({
  sequence,
  strings,
}: {
  sequence: readonly PatternMatch[];
  strings: PasswordStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <ol className="space-y-2">
      {sequence.map((match) => (
        <li
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-border border-b pb-2 last:border-0"
          key={`${match.i}-${match.j}`}
        >
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
            {match.token.replace(SPACE, "␣")}
          </code>
          <span className="text-foreground text-sm">
            {describe(match, strings, locale, fmt)}
          </span>
          <span className="ml-auto text-muted-foreground text-xs">
            {t(strings.matchGuesses, {
              guesses: guessText(Math.log10(Math.max(match.guesses, 1)), fmt),
            })}
          </span>
        </li>
      ))}
    </ol>
  );
}

const SUGGESTION_KEYS: Record<SuggestionId, keyof PasswordStrings> = {
  common: "suggestCommon",
  commonPart: "suggestCommonPart",
  words: "suggestWords",
  l33t: "suggestL33t",
  capitals: "suggestCapitals",
  reversed: "suggestReversed",
  sequence: "suggestSequence",
  repeat: "suggestRepeat",
  keyboard: "suggestKeyboard",
  date: "suggestDate",
  length: "suggestLength",
  good: "suggestGood",
};

export function Suggestions({
  ids,
  strings,
}: {
  ids: readonly SuggestionId[];
  strings: PasswordStrings;
}) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-foreground text-sm leading-relaxed">
      {ids.map((id) => (
        <li key={id}>{strings[SUGGESTION_KEYS[id]]}</li>
      ))}
    </ul>
  );
}
